import { useMemo, useState } from 'react';
import { useResolvedPath } from 'react-router-dom';
import {
  BreadcrumbEntry,
  PageLayout,
  PageLayoutProps,
  useBreadcrumbEntries,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import { makeStyles } from '@material-ui/core';
import { CONTENT_MAX_WIDTH } from './layout';
import { PageHeader } from './PageHeader';
import { PageSubtitleSlotContext } from './PageSubtitle';

const useStyles = makeStyles(theme => ({
  // The top bar's column plus the 24px gutters the header and the pages keep
  // inside it, so their edges line up with the search and the avatar.
  column: {
    maxWidth: CONTENT_MAX_WIDTH + theme.spacing(6),
    margin: '0 auto',
  },
}));

/**
 * Page layout of every new-frontend-system page, with the BYX header in
 * place of the stock plugin header, centered like the design. Tab paths and
 * the breadcrumb entry are resolved exactly like the stock layout, so nested
 * pages keep their trail.
 */
export function ByxPageLayout(props: Readonly<PageLayoutProps>) {
  const { title, icon, noHeader, titleLink, headerActions, tabs, children } =
    props;
  const classes = useStyles();
  const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');
  const { items: breadcrumbs } = useBreadcrumbEntries();
  // The header's subtitle slot, for pages that show one (PageSubtitle).
  const [subtitleSlot, setSubtitleSlot] = useState<HTMLElement | null>(null);

  const resolvedTabs = useMemo(
    () =>
      tabs?.map(tab => ({
        id: tab.id,
        label: tab.label,
        href: tab.href.startsWith('/')
          ? tab.href
          : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
      })),
    [tabs, parentPath],
  );

  if (noHeader) {
    return <>{children}</>;
  }

  const content = (
    <div className={classes.column}>
      <PageHeader
        title={title ?? ''}
        titleLink={titleLink}
        icon={icon}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        tabs={resolvedTabs}
        subtitleRef={setSubtitleSlot}
      />
      <PageSubtitleSlotContext.Provider value={subtitleSlot}>
        {children}
      </PageSubtitleSlotContext.Provider>
    </div>
  );

  if (!title) {
    return content;
  }
  return (
    <BreadcrumbEntry
      entry={{ label: title, href: titleLink ?? (parentPath || '/') }}
    >
      {content}
    </BreadcrumbEntry>
  );
}

/**
 * Replaces the app's `core-page-layout` (same name, so it is an override and
 * not a second implementation competing for the same component).
 */
export const byxPageLayout = SwappableComponentBlueprint.make({
  name: 'core-page-layout',
  params: define =>
    define({
      component: PageLayout,
      loader: () => ByxPageLayout,
    }),
});
